import type { TFunction } from 'i18next';
import { isClosedOrderStatus } from '../closedOrders';
import api from '../../../services/api';
import { submitWithOperationalException } from '../../operationalExceptions/operationalExceptionFlow';
import { normalizeTransportRouteDetail, normalizeTransportRoutes, TransportRouteDetailDto, TransportRouteSummaryDto } from '../../../dto/transport';

export interface TransportRouteListFilters {
  search?: string;
  transportCompany?: string;
  products?: string;
  status?: string;
  orderDateFrom?: string;
  orderDateTo?: string;
  /** Departure window (ISO 8601), used by the dashboard's upcoming-transports widget. */
  dateFrom?: string;
  dateTo?: string;
  /** WP14: INBOUND (przychodzące) / OUTBOUND (wychodzące). */
  direction?: 'INBOUND' | 'OUTBOUND';
  /** Decision 2026-10-04: rodzaj auta (derived on the server). */
  kind?: 'FACTORY_TO_CUSTOMER' | 'FACTORY_TO_WAREHOUSE' | 'WAREHOUSE_TO_CUSTOMER';
}

export async function listTransportRoutes(
  filters: TransportRouteListFilters = {},
): Promise<TransportRouteSummaryDto[]> {
  const response = await api.get('/transport-routes', { params: filters });
  return normalizeTransportRoutes(response.data, 'transportRoutes.list');
}

export async function listTransportRoutesForSelection(): Promise<TransportRouteSummaryDto[]> {
  const response = await api.get('/transport-routes');
  return normalizeTransportRoutes(response.data, 'transportRoutes.selection');
}

export async function fetchTransportRoute(transportId: string): Promise<TransportRouteDetailDto> {
  const response = await api.get(`/transport-routes/${encodeURIComponent(transportId)}`);
  return normalizeTransportRouteDetail(response.data, 'transportRoutes.detail');
}

export async function createTransportRoute(payload: Record<string, unknown>) {
  return api.post('/transport-routes', payload);
}

export async function updateTransportRoute(transportId: string, payload: Record<string, unknown>) {
  return api.put(`/transport-routes/${encodeURIComponent(transportId)}`, payload);
}

export interface TransportInvoicePayload {
  invoiceNumber: string;
  /** YYYY-MM-DD */
  invoiceIssueDate: string;
  invoiceAmount: number;
  invoiceCurrency: string;
}

/** Faktura przewoźnika: record or correct; its amount replaces the planned rate. */
export async function saveTransportInvoice(
  transportId: string,
  payload: TransportInvoicePayload,
): Promise<TransportRouteDetailDto> {
  await api.put(`/transport-routes/${encodeURIComponent(transportId)}/invoice`, payload);
  return fetchTransportRoute(transportId);
}

/** Removes the invoice; the planned rate comes back. */
export async function removeTransportInvoice(transportId: string): Promise<TransportRouteDetailDto> {
  await api.delete(`/transport-routes/${encodeURIComponent(transportId)}/invoice`);
  return fetchTransportRoute(transportId);
}

/** Update, then return the canonical detail read model rather than local payload assumptions. */
export async function updateTransportRouteAndRefetch(
  transportId: string,
  payload: Record<string, unknown>,
): Promise<TransportRouteDetailDto> {
  await updateTransportRoute(transportId, payload);
  return fetchTransportRoute(transportId);
}

/**
 * Delete a transport (shared or single-order). Transports live under
 * /transport-routes regardless of how many orders they carry, so the deletion
 * seam belongs here rather than under any order-scoped API module.
 */
/**
 * WP12 (1.16): a transport that WZ/PZ documents point at is deleted after a
 * confirmed warning (the links are removed with it). With `confirmation` the
 * shared operational-exception dialog asks for the reason; the confirmation
 * travels in the DELETE body. Returns false when the operator cancelled.
 */
export interface TransportMutationConfirmation {
  modal: Parameters<typeof submitWithOperationalException>[2];
  t: TFunction;
}

async function deleteWithConfirmation(
  url: string,
  confirmation?: TransportMutationConfirmation,
) {
  if (!confirmation) return api.delete(url);
  return submitWithOperationalException(
    (body) => api.delete(url, { data: body }),
    {},
    confirmation.modal,
    confirmation.t,
  );
}

export async function deleteTransportRoute(
  transportId: string,
  confirmation?: TransportMutationConfirmation,
): Promise<boolean> {
  const response = await deleteWithConfirmation(
    `/transport-routes/${encodeURIComponent(transportId)}`,
    confirmation,
  );
  return response !== null;
}

export type UnlinkTransportOrderResult =
  | { deleted: true; route: null }
  | { deleted: false; route: TransportRouteDetailDto }
  | { cancelled: true; deleted: false; route: null };

/**
 * Unlink through the canonical TransportOrderAllocation endpoint. If the
 * transport survives, immediately refetch its canonical detail projection.
 */
export async function unlinkTransportOrderAndRefetch(
  transportId: string,
  orderId: string,
  confirmation?: TransportMutationConfirmation,
): Promise<UnlinkTransportOrderResult> {
  const response = await deleteWithConfirmation(
    `/transport-routes/${encodeURIComponent(transportId)}/orders/${encodeURIComponent(orderId)}`,
    confirmation,
  );
  if (response === null) return { cancelled: true, deleted: false, route: null };
  const deleted = response.data?.deleted === true;

  if (deleted) return { deleted: true, route: null };
  return { deleted: false, route: await fetchTransportRoute(transportId) };
}

/**
 * „Dołóż do istniejącego auta” (decision 2026-10-04): one ZK or one ZD onto a
 * saved truck; omitted quantities = the whole rest (server-computed). A
 * confirmable warning (e.g. over-allocation) goes through the shared
 * operational-exception dialog. Returns null when the operator cancelled.
 */
export type TransportAttachInput =
  | { orderId: string; cargoItems?: Array<{ orderProductId: string; quantity: number; unit?: string }> }
  | { supplierOrderId: string; supplierCargoItems?: Array<{ supplierOrderItemId: string; quantity: number; unit: string }> };

export interface TransportAttachResult {
  route: TransportRouteDetailDto;
  /** Raw response for the shared save warnings (`showTransportSaveWarnings`). */
  raw: unknown;
  /** What the server added to the route (new unloading/loading place or null). */
  attached: { orderId?: string; supplierOrderId?: string; deliveryAddress?: string | null; loadingAddress?: string | null } | null;
}

export async function attachOrderToTransportRoute(
  transportId: string,
  input: TransportAttachInput,
  confirmation: TransportMutationConfirmation,
): Promise<TransportAttachResult | null> {
  const response = await submitWithOperationalException(
    (body) => api.post(`/transport-routes/${encodeURIComponent(transportId)}/orders`, body),
    input as Record<string, unknown>,
    confirmation.modal,
    confirmation.t,
  );
  if (!response) return null;
  return {
    route: normalizeTransportRouteDetail(response.data, 'transportRoutes.attach'),
    raw: response.data,
    attached: response.data?.attached ?? null,
  };
}

/**
 * Today's NBP rate of a truck currency for the cost-split preview only (the
 * save takes its own FX snapshot). null = no rate.
 */
export async function fetchTransportPreviewRate(
  currency: string,
  date: string,
): Promise<{ rate: number; effectiveDate: string | null } | null> {
  const { data } = await api.get('/exchange-rates/quote', { params: { currency, date } });
  const rate = Number(data?.rate);
  return Number.isFinite(rate) && rate > 0 ? { rate, effectiveDate: data?.effectiveDate ?? null } : null;
}

/** One order offered by „Dołóż zamówienie” on the transport list. */
export interface TransportAttachCandidateOrder {
  type: 'order' | 'supplierOrder';
  id: string;
  /** Operator-facing number. */
  label: string;
  /** Customer or supplier name. */
  party: string;
  customerPickup: boolean;
  /** The whole order is already planned on trucks, by quantities (ZD `truckCoverage`, ZK `/order-coverage`). */
  fullyOnTrucks?: boolean;
}


export interface CustomerOrderTruckMarks {
  /** NONE / PARTIAL / FULL — by quantities. */
  coverage: Record<string, string>;
  /** The active trucks each ZK already rides. */
  trucks: Record<string, Array<{ id: string; label: string }>>;
}

/**
 * Usability round 2026-10-05: which trucks the given ZK already ride (and
 * whether wholly), for the order picker of the truck form. Advisory: a
 * failed lookup returns empty marks.
 */
export async function fetchCustomerOrderTruckMarks(orderIds: string[]): Promise<CustomerOrderTruckMarks> {
  if (orderIds.length === 0) return { coverage: {}, trucks: {} };
  try {
    const response = await api.post('/transport-routes/order-coverage', { orderIds });
    const trucks: CustomerOrderTruckMarks['trucks'] = {};
    Object.entries((response.data?.trucks || {}) as Record<string, unknown>).forEach(([orderId, rows]) => {
      trucks[orderId] = (Array.isArray(rows) ? rows : [])
        .map((row: any) => ({ id: String(row?.id || ''), label: String(row?.label || '') }))
        .filter((row) => row.id && row.label);
    });
    return { coverage: (response.data?.coverage || {}) as Record<string, string>, trucks };
  } catch {
    return { coverage: {}, trucks: {} };
  }
}

/**
 * Candidates of „Dołóż zamówienie” (usability round 2026-10-05): open ZK
 * and/or ZD, depending on what the truck kind carries. Quantities and the
 * rest are the server's on the POST.
 */
export async function fetchTransportAttachCandidates(
  types: Array<'order' | 'supplierOrder'>,
): Promise<TransportAttachCandidateOrder[]> {
  const [orders, supplierOrders] = await Promise.all([
    types.includes('order') ? api.get('/orders') : Promise.resolve(null),
    types.includes('supplierOrder') ? api.get('/supplier-orders') : Promise.resolve(null),
  ]);
  const rows: TransportAttachCandidateOrder[] = [];
  const orderRecords: any[] = Array.isArray(orders?.data) ? orders!.data : [];
  const openOrders = orderRecords
    .filter((record) => record && !record.isQuote && !isClosedOrderStatus(record.status));
  // Round 3 (U15): ZK already wholly on trucks are greyed out. Advisory: a
  // failed lookup leaves them selectable (the POST still refuses them).
  const coverage: Record<string, string> = openOrders.length > 0
    ? await api.post('/transport-routes/order-coverage', {
      orderIds: openOrders.map((record) => String(record.id)),
    }).then((response) => response.data?.coverage || {}).catch(() => ({}))
    : {};
  openOrders.forEach((record) => rows.push({
    type: 'order',
    id: String(record.id),
    label: String(record.displayOrderNumber || record.orderNumber || record.id),
    party: String(record.customer?.name || record.customerName || ''),
    customerPickup: record.deliveryMode === 'CUSTOMER_PICKUP',
    fullyOnTrucks: coverage[String(record.id)] === 'FULL',
  }));
  const supplierRecords: any[] = Array.isArray(supplierOrders?.data) ? supplierOrders!.data : [];
  supplierRecords
    .filter((record) => record && !isClosedOrderStatus(record.status) && !record.archivedAt)
    .forEach((record) => rows.push({
      type: 'supplierOrder',
      id: String(record.id),
      label: String(record.displayOrderNumber || record.manualOrderNumber || record.orderNumber || record.id),
      party: String(record.supplier?.name || record.supplierName || ''),
      customerPickup: false,
      fullyOnTrucks: record.truckCoverage === 'FULL',
    }));
  return rows;
}

/** Form defaults: our warehouse address (fixed loading/unloading place). */
export async function fetchTransportFormDefaults(): Promise<{ warehouseAddress: string | null }> {
  const response = await api.get('/transport-routes/defaults');
  const address = typeof response.data?.warehouseAddress === 'string' ? response.data.warehouseAddress.trim() : '';
  return { warehouseAddress: address || null };
}
